import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";

import { api, type RaceSummary } from "@/api";
import { trackTitle } from "@/features/racing/venue";
import { scheduleWindowAt } from "@/app/prefetch";
import { OverlaySelect } from "@/components/OverlaySelect";

import { RaceDetails } from "./RaceDetails";
import { ScheduleCalendar } from "./ScheduleCalendar";
import { ScheduleRaceRail } from "./ScheduleRaceRail";
import {
  ScheduleEmpty,
  ScheduleError,
  ScheduleSkeleton,
} from "./ScheduleStates";
import {
  adjacentWindow,
  localDateTime,
  narrowerWindow,
  scheduleKey,
  scheduleOptions,
  SCHEDULE_LIMIT,
  calendarInWindow,
  type ScheduleState,
} from "./scheduleState";

export function UpcomingSchedule({
  state,
  now,
  onChange,
}: {
  state: ScheduleState;
  now: number;
  onChange: (next: ScheduleState, replace?: boolean) => void;
}) {
  const client = useQueryClient();
  const request = useRef(0);
  const [checking, setChecking] = useState<-1 | 1 | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<RaceSummary | null>(null);
  const [focusNow, setFocusNow] = useState(0);
  const detailsId = useId();
  const schedule = useQuery({
    queryKey: scheduleKey(state.window),
    // Keep this unfiltered: league choices belong to the whole displayed window.
    queryFn: () => api.schedule(state.window.from, state.window.to),
    staleTime: 10_000,
    refetchInterval: 20_000,
    placeholderData: keepPreviousData,
  });
  const { leagues, distances } = scheduleOptions(schedule.data);
  const races = calendarInWindow(
    schedule.data?.races ?? [],
    state.window,
    state.league,
    state.distance,
  );
  const capped = (schedule.data?.races.length ?? 0) >= SCHEDULE_LIMIT;
  const hasFilters = Boolean(state.league || state.distance);
  const selectedRace =
    schedule.data?.races.find((race) => race.raceId === selected?.raceId) ??
    selected;
  const tracks = leagues.filter(
    (track) => !state.league || track.slug === state.league,
  );

  function select(race: RaceSummary) {
    setSelected(selected?.raceId === race.raceId ? null : race);
  }

  function resumeLive() {
    request.current += 1;
    setChecking(null);
    setNotice(null);
    setFocusNow((value) => value + 1);
    onChange({ ...state, follow: true, window: scheduleWindowAt(Date.now()) });
  }

  useEffect(() => {
    request.current += 1;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) {
        setChecking(null);
        setNotice(null);
      }
    });
    return () => {
      cancelled = true;
      request.current += 1;
    };
  }, [state.window.from, state.window.to, state.league, state.distance]);

  async function browse(direction: -1 | 1) {
    const token = ++request.current;
    const next = adjacentWindow(state.window, direction);
    setChecking(direction);
    setNotice(null);
    if (state.follow) onChange({ ...state, follow: false }, true);
    try {
      // There is no range/cursor metadata. Probe exactly one adjacent window
      // on intent; do not invent a horizon or walk indefinitely through gaps.
      const published = await client.fetchQuery({
        queryKey: scheduleKey(next),
        queryFn: () => api.schedule(next.from, next.to),
        staleTime: 10_000,
      });
      if (token !== request.current) return;
      if (published.races.length)
        onChange({ ...state, window: next, follow: false });
      else
        setNotice(
          `No published races in the ${direction < 0 ? "earlier" : "later"} adjacent window. Your displayed range has not changed.`,
        );
    } catch {
      if (token === request.current)
        setNotice(
          "The adjacent window could not be checked. Your displayed range has not changed; try again.",
        );
    } finally {
      if (token === request.current) setChecking(null);
    }
  }

  const leagueOptions = [
    { value: "", label: "All leagues" },
    ...leagues.map((league) => ({ value: league.slug, label: league.name })),
    ...(state.league && !leagues.some((league) => league.slug === state.league)
      ? [{ value: state.league, label: `${state.league} (not in this window)` }]
      : []),
  ];
  const distanceOptions = [
    { value: "", label: "All distances" },
    ...distances.map((distance) => ({
      value: String(distance),
      label: `${distance}m`,
    })),
    ...(state.distance && !distances.includes(Number(state.distance))
      ? [
          {
            value: state.distance,
            label: `${state.distance}m (not in this window)`,
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="schedule-toolbar">
        <div className="schedule-window">
          <strong>Displayed window</strong>
          <span>
            <time dateTime={state.window.from}>
              {localDateTime(state.window.from)}
            </time>
            <span aria-hidden="true"> — </span>
            <time dateTime={state.window.to}>
              {localDateTime(state.window.to)}
            </time>
          </span>
        </div>
        <div className="schedule-window-actions" aria-label="Schedule window">
          <button
            className="schedule-button"
            type="button"
            disabled={checking !== null}
            onClick={() => void browse(-1)}
          >
            {checking === -1 ? "Checking…" : "Earlier"}
          </button>
          <button
            className="schedule-button"
            type="button"
            aria-pressed={state.follow}
            title="Return to Now and follow live"
            onClick={resumeLive}
          >
            Now
          </button>
          <button
            className="schedule-button"
            type="button"
            disabled={checking !== null}
            onClick={() => void browse(1)}
          >
            {checking === 1 ? "Checking…" : "Later"}
          </button>
        </div>
      </div>
      <div className="schedule-filters">
        <OverlaySelect
          ariaLabel="League in displayed window"
          value={state.league}
          options={leagueOptions}
          onChange={(league) => onChange({ ...state, league })}
        />
        <OverlaySelect
          ariaLabel="Distance in displayed window"
          value={state.distance}
          options={distanceOptions}
          onChange={(distance) => onChange({ ...state, distance })}
        />
        {hasFilters ? (
          <button
            type="button"
            className="schedule-button"
            onClick={() => onChange({ ...state, league: "", distance: "" })}
          >
            Clear filters
          </button>
        ) : null}
      </div>
      <p className="schedule-scope">
        {state.follow
          ? "Following live. "
          : "Browsing · select Now to follow live. "}
        Filters apply only to this displayed window. Scroll to browse, or use
        Earlier and Later for adjacent published windows.
      </p>
      {notice ? (
        <p className="schedule-notice" role="status">
          {notice}
        </p>
      ) : null}
      {capped ? (
        <div className="schedule-notice" role="status">
          <strong>This window reached the 2,000-race response limit.</strong>
          <p>
            Some races and filter options may be missing. Narrow the time range
            to see a more complete window.
          </p>
          <button
            className="schedule-button"
            type="button"
            disabled={
              Date.parse(state.window.to) - Date.parse(state.window.from) <= 1
            }
            onClick={() =>
              onChange({
                ...state,
                window: narrowerWindow(state.window),
                follow: false,
              })
            }
          >
            Halve window
          </button>
        </div>
      ) : null}
      {schedule.isPending ? (
        <ScheduleSkeleton calendar />
      ) : schedule.isError && !schedule.data ? (
        <ScheduleError
          title="Schedule could not be loaded"
          retry={() => void schedule.refetch()}
        />
      ) : (
        <>
          {schedule.isError ? (
            <ScheduleError
              title="Schedule could not be refreshed"
              retry={() => void schedule.refetch()}
            />
          ) : null}
          {schedule.isPlaceholderData ? (
            <p className="schedule-scope" role="status">
              Updating this time window…
            </p>
          ) : null}
          <ScheduleCalendar
            races={races}
            tracks={tracks}
            window={state.window}
            now={now}
            follow={state.follow}
            focusNow={focusNow}
            selected={selected?.raceId ?? null}
            detailsId={detailsId}
            onSelect={select}
            onBrowse={() => onChange({ ...state, follow: false }, true)}
          />
          {!races.length ? (
            <ScheduleEmpty
              title={
                hasFilters
                  ? "No matching upcoming races"
                  : "No upcoming races in this window"
              }
              detail={
                capped
                  ? "This response may be incomplete. Halve the window before drawing conclusions from these filters."
                  : hasFilters
                    ? "Clear the filters or check another published window."
                    : "Published upcoming races will appear here as the schedule updates. Completed races are in Results."
              }
            />
          ) : null}
          <ScheduleRaceRail
            races={races}
            now={now}
            selected={selected?.raceId ?? null}
            detailsId={detailsId}
            onSelect={select}
          />
          <section
            id={detailsId}
            className="schedule-selection"
            aria-label="Selected race details"
            hidden={!selectedRace}
          >
            {selectedRace ? (
              <>
                <header className="schedule-details__heading">
                  <strong>
                    {trackTitle(selectedRace)} · {selectedRace.distanceM}m ·{" "}
                    {localDateTime(selectedRace.scheduledAt)}
                  </strong>
                  <button
                    type="button"
                    className="schedule-button"
                    onClick={() => setSelected(null)}
                  >
                    Close details
                  </button>
                </header>
                <RaceDetails
                  key={selectedRace.raceId}
                  raceId={selectedRace.raceId}
                  official={selectedRace.status === "official"}
                  racecardHash={selectedRace.racecardHash}
                />
              </>
            ) : null}
          </section>
        </>
      )}
    </>
  );
}
